import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/render/project/src/node_modules/.pnpm/gatsby-theme-docz@2.3.1_@babel+core@7.11.1_@types+node@24.7.0_docz@2.4.0_@babel+core@7._04842b0c5656885ffb9b2f355e30df2b/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "lowkeyui-living-styleguide-implementation-plan"
    }}>{`LowkeyUI Living Styleguide Implementation Plan`}</h1>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`For agentic workers:`}</strong>{` REQUIRED SUB-SKILL: Use superpowers:subagent-driven-development (recommended) or superpowers:executing-plans to implement this plan task-by-task. Steps use checkbox (`}<inlineCode parentName="p">{`- [ ]`}</inlineCode>{`) syntax for tracking.`}</p>
    </blockquote>
    <p><strong parentName="p">{`Goal:`}</strong>{` A dev-only `}<inlineCode parentName="p">{`/styleguide`}</inlineCode>{` route in builder-ui that renders every real lowkeyui component with source pointers, plus a script that exports it as a self-contained HTML snapshot.`}</p>
    <p><strong parentName="p">{`Architecture:`}</strong>{` A React page (`}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/`}</inlineCode>{`) imports real components / real `}<inlineCode parentName="p">{`kp-*`}</inlineCode>{` markup — never copied CSS — registered behind `}<inlineCode parentName="p">{`import.meta.env.DEV`}</inlineCode>{` via `}<inlineCode parentName="p">{`route.lazy`}</inlineCode>{`. A Node script drives headless Chrome to snapshot the rendered page with its compiled CSS into `}<inlineCode parentName="p">{`__drops/lowkey-components.html`}</inlineCode>{`. Components are added section-by-section with per-section user approval.`}</p>
    <p><strong parentName="p">{`Tech Stack:`}</strong>{` React 18 + react-router v7 (`}<inlineCode parentName="p">{`route.lazy`}</inlineCode>{`), Tailwind (compiled via `}<inlineCode parentName="p">{`app.css`}</inlineCode>{`), vitest + `}<inlineCode parentName="p">{`renderPage`}</inlineCode>{` from `}<inlineCode parentName="p">{`@testing/setup`}</inlineCode>{`, `}<inlineCode parentName="p">{`playwright-core`}</inlineCode>{` (channel: chrome) for export.`}</p>
    <p><strong parentName="p">{`Environment notes (read first):`}</strong></p>
    <ul>
      <li parentName="ul">{`Repo path on host: `}<inlineCode parentName="li">{`~/.config/af/agents/a1/workspace/builder-ui`}</inlineCode>{` (bind-mounted into container `}<inlineCode parentName="li">{`a1`}</inlineCode>{` at `}<inlineCode parentName="li">{`~/workspace/builder-ui`}</inlineCode>{`).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`node_modules`}</inlineCode>{` contains `}<strong parentName="li">{`linux`}</strong>{` native binaries — run pnpm/vitest `}<strong parentName="li">{`inside the container`}</strong>{`: `}<inlineCode parentName="li">{`ssh -p 2223 agent@localhost 'cd ~/workspace/builder-ui && <cmd>'`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The dev server is already running; the app is at `}<inlineCode parentName="li">{`https://local.kualibuild.com:4001`}</inlineCode>{` (self-signed cert). Vite hot-reloads file changes — no restarts needed.`}</li>
      <li parentName="ul">{`Work on branch `}<inlineCode parentName="li">{`styleguide-lowkey`}</inlineCode>{` (already created; spec committed).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`.lowkey`}</inlineCode>{`/`}<inlineCode parentName="li">{`.lowkey-forms`}</inlineCode>{` are normally added to `}<inlineCode parentName="li">{`<html>`}</inlineCode>{` by feature flags (`}<inlineCode parentName="li">{`app/src/index.jsx:82-86`}</inlineCode>{`); the styleguide page adds its own wrapper div so it renders correctly regardless of flag state.`}</li>
    </ul>
    <hr></hr>
    <h3 {...{
      "id": "task-1-page-scaffold-dev-only-route-smoke-test"
    }}>{`Task 1: Page scaffold, dev-only route, smoke test`}</h3>
    <p><strong parentName="p">{`Files:`}</strong></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/index.jsx`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/section.jsx`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/__tests__/index.test.jsx`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Modify: `}<inlineCode parentName="p">{`app/src/routes.jsx`}</inlineCode>{` (children of `}<inlineCode parentName="p">{`RootLayout`}</inlineCode>{`, next to the `}<inlineCode parentName="p">{`debug/:id/*`}</inlineCode>{` entry at ~line 501)`}</p>
      </li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}>
        <p parentName="li"><input parentName="p" {...{
            "type": "checkbox",
            "checked": false,
            "disabled": true
          }}></input>{` `}<strong parentName="p">{`Step 1: Write the failing smoke test`}</strong></p>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/* Copyright © 2017-2026 Kuali, Inc. - All Rights Reserved */
import { screen } from '@testing-library/react'
import { renderPage } from '@testing/setup'

import { Component as Styleguide } from '../index'

describe('<Styleguide />', () => {
  test('renders banner and warning', async () => {
    await renderPage(<Styleguide />)
    screen.getByText('LowkeyUI Styleguide')
    screen.getByText(/do not copy styles from this page/i)
  })
})
`}</code></pre>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2: Run test to verify it fails`}</strong></li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`ssh -p 2223 agent@localhost 'cd ~/workspace/builder-ui && pnpm test:no-coverage app/src/pages-builder/styleguide'`}</inlineCode>{`
Expected: FAIL — cannot resolve `}<inlineCode parentName="p">{`../index`}</inlineCode></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3: Create `}<inlineCode parentName="strong">{`section.jsx`}</inlineCode></strong>{` (the per-component frame; its own chrome uses plain stone Tailwind classes — deliberately distinct from the components it displays)`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/* Copyright © 2017-2026 Kuali, Inc. - All Rights Reserved */
import React from 'react'

export function Section ({ num, title, source, code, children }) {
  return (
    <section className='mt-10'>
      <h2 className='flex items-center gap-2 text-xl font-bold text-stone-900'>
        <span className='rounded-md bg-stone-900 px-2 py-0.5 text-sm font-semibold text-stone-50'>
          {num}
        </span>
        {title}
      </h2>
      <div className='mt-1 text-xs text-stone-500'>
        Source of truth: <code>{source}</code>
      </div>
      {code && (
        <pre className='mt-2 overflow-x-auto rounded-md bg-stone-200 p-2 text-xs text-stone-800'>
          {code}
        </pre>
      )}
      <div className='mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-stone-300 bg-white p-5'>
        {children}
      </div>
    </section>
  )
}

export function Swatch ({ label, children }) {
  return (
    <div className='flex flex-col items-start gap-1'>
      <span className='text-[11px] uppercase tracking-wide text-stone-400'>
        {label}
      </span>
      {children}
    </div>
  )
}
`}</code></pre>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4: Create `}<inlineCode parentName="strong">{`index.jsx`}</inlineCode></strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/* Copyright © 2017-2026 Kuali, Inc. - All Rights Reserved */
import React from 'react'

export function Component () {
  return (
    <div className='lowkey lowkey-forms min-h-screen bg-stone-100 p-8'>
      <div className='mx-auto max-w-5xl' data-styleguide-ready>
        <h1 className='text-3xl font-bold text-stone-900'>
          LowkeyUI Styleguide
        </h1>
        <div className='mt-4 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900'>
          <strong>For AI sessions and devs:</strong> do not copy styles from this
          page. Import these components or use these class names — every section
          lists its source of truth. Lowkey styles live in{' '}
          <code>app/src/app.css</code> (the <code>.lowkey</code> /{' '}
          <code>.lowkey-forms</code> blocks), <code>app/src/ui/</code>,{' '}
          <code>app/src/ui/shadcn/</code>, and <code>tailwind.config.js</code>.
        </div>
        {/* Component sections are appended here, one per inventory item. */}
      </div>
    </div>
  )
}

export default Component
`}</code></pre>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 5: Register the dev-only route`}</strong></li>
    </ul>
    <p>{`In `}<inlineCode parentName="p">{`app/src/routes.jsx`}</inlineCode>{`, inside the `}<inlineCode parentName="p">{`RootLayout`}</inlineCode>{` children, directly after `}<inlineCode parentName="p">{`{ path: 'debug/:id/*', element: <Debug /> },`}</inlineCode>{` add:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`...(import.meta.env.DEV
  ? [{ path: 'styleguide', lazy: () => import('./pages-builder/styleguide') }]
  : []),
`}</code></pre>
    <p>{`(`}<inlineCode parentName="p">{`route.lazy`}</inlineCode>{` loads the module's `}<inlineCode parentName="p">{`Component`}</inlineCode>{` export on demand; in prod builds Vite replaces `}<inlineCode parentName="p">{`import.meta.env.DEV`}</inlineCode>{` with `}<inlineCode parentName="p">{`false`}</inlineCode>{` and the route — and its chunk — is eliminated. This mirrors the existing `}<inlineCode parentName="p">{`showDevRulePresentation`}</inlineCode>{` conditional-spread pattern at routes.jsx:502-504.)`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 6: Run test to verify it passes`}</strong></li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`ssh -p 2223 agent@localhost 'cd ~/workspace/builder-ui && pnpm test:no-coverage app/src/pages-builder/styleguide'`}</inlineCode>{`
Expected: PASS (1 test)`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 7: Verify live, including auth behavior`}</strong></li>
    </ul>
    <p>{`Open `}<inlineCode parentName="p">{`https://local.kualibuild.com:4001/styleguide`}</inlineCode>{` in a browser (Playwright MCP or the user's browser). Expected: title + amber banner render.
`}<strong parentName="p">{`Contingency:`}</strong>{` if `}<inlineCode parentName="p">{`RootLayout`}</inlineCode>{` forces a login redirect, that's acceptable for the live page but matters for the export script — note it, and in Task 4 pass a logged-in `}<inlineCode parentName="p">{`storageState`}</inlineCode>{` to Playwright (or move the route next to `}<inlineCode parentName="p">{`{ path: 'health' }`}</inlineCode>{` which serves unauthenticated). Record which path was taken in the commit message.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 8: Commit`}</strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`cd ~/.config/af/agents/a1/workspace/builder-ui
git add app/src/pages-builder/styleguide app/src/routes.jsx
git commit -m "feat(styleguide): dev-only /styleguide route with scaffold"
`}</code></pre>
    <hr></hr>
    <h3 {...{
      "id": "task-2-discovery-sweep--finalize-the-component-inventory"
    }}>{`Task 2: Discovery sweep — finalize the component inventory`}</h3>
    <p><strong parentName="p">{`Files:`}</strong></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`docs/superpowers/specs/2026-06-10-lowkey-styleguide-inventory.md`}</inlineCode></p>
      </li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}>
        <p parentName="li"><input parentName="p" {...{
            "type": "checkbox",
            "checked": false,
            "disabled": true
          }}></input>{` `}<strong parentName="p">{`Step 1: Sweep app.css for every `}<inlineCode parentName="strong">{`kp-*`}</inlineCode>{` class styled under `}<inlineCode parentName="strong">{`.lowkey`}</inlineCode>{`/`}<inlineCode parentName="strong">{`.lowkey-forms`}</inlineCode></strong></p>
      </li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`awk '/\\.lowkey/,/^}/' app/src/app.css | grep -oE 'kp-[a-z-]+' | sort -u`}</inlineCode>{`
Cross-check by reading `}<inlineCode parentName="p">{`app/src/app.css:234-720`}</inlineCode>{` directly (some selectors span lines).`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2: List every ui component`}</strong></li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`ls app/src/ui app/src/ui/shadcn`}</inlineCode>{`
Known so far: alerts, checkbox, config-box, indeterminate-checkbox, info-box, input, lookup, pop-up, popover, radios, segmented-control, tabs, theme, toggle, tooltip; shadcn: dropdown-menu, popover, separator, sheet, sidebar, skeleton.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3: Find inline lowkey-variant styling and flag-switched components`}</strong></li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`grep -rl "lowkey:" app/src --include='*.jsx' --include='*.tsx' | sort`}</inlineCode>{` and `}<inlineCode parentName="p">{`grep -rl "lowKeyUi\\|lowKeyForms" app/src/components --include='*.jsx' | sort`}</inlineCode>{`
These reveal components styled with the `}<inlineCode parentName="p">{`lowkey:`}</inlineCode>{` Tailwind variant or branching on the flags — candidates the old HTML doc missed.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4: Identify the 1/2/3-panel layout patterns`}</strong></li>
    </ul>
    <p>{`Read `}<inlineCode parentName="p">{`app/src/components/app-layout.jsx`}</inlineCode>{`, `}<inlineCode parentName="p">{`app/src/components/home-layout.jsx`}</inlineCode>{`, `}<inlineCode parentName="p">{`app/src/components/sidebar/index.jsx`}</inlineCode>{`, and one representative screen per shape (e.g. dashboard = sidebar + content; form builder = 3-panel). Record the component/classes that produce each layout.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 5: Write the inventory doc`}</strong></li>
    </ul>
    <p><inlineCode parentName="p">{`docs/superpowers/specs/2026-06-10-lowkey-styleguide-inventory.md`}</inlineCode>{`: a checklist table — section number, name, kind (atom/molecule/layout), real source file(s), seed-doc status (verified / corrected / hallucinated-dropped / newly-discovered). Order: form controls → buttons-adjacent → data display → feedback → overlays → navigation → molecules → layouts. Buttons are already Task 3; number the rest after it.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 6: Commit and present to user`}</strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add docs/superpowers/specs/2026-06-10-lowkey-styleguide-inventory.md
git commit -m "docs(styleguide): component inventory from discovery sweep"
`}</code></pre>
    <p>{`Show the user the inventory summary (counts: verified/corrected/dropped/new) and get approval of the build order before continuing.`}</p>
    <hr></hr>
    <h3 {...{
      "id": "task-3-buttons-section-template-for-all-later-sections"
    }}>{`Task 3: Buttons section (template for all later sections)`}</h3>
    <p><strong parentName="p">{`Files:`}</strong></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`Modify: `}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/index.jsx`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`app/src/pages-builder/styleguide/sections/buttons.jsx`}</inlineCode></p>
      </li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}>
        <p parentName="li"><input parentName="p" {...{
            "type": "checkbox",
            "checked": false,
            "disabled": true
          }}></input>{` `}<strong parentName="p">{`Step 1: Re-read the real button styles before writing markup`}</strong></p>
      </li>
    </ul>
    <p>{`Read `}<inlineCode parentName="p">{`app/src/app.css:234-378`}</inlineCode>{`. The variants that exist (everything else is hallucination): `}<inlineCode parentName="p">{`kp-button-primary`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-secondary`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-destructive`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-outline`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-ghost`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-link`}</inlineCode>{` (standard group); `}<inlineCode parentName="p">{`kp-button-with-icon`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-primary-with-icon`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-spinner-icon`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-dropdown`}</inlineCode>{` (icon group); `}<inlineCode parentName="p">{`kp-button-transparent`}</inlineCode>{` (`}<inlineCode parentName="p">{`.lowkey-forms`}</inlineCode>{`, app.css:356-378); modifiers `}<inlineCode parentName="p">{`kp-button-sm`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-icon-only`}</inlineCode>{`, `}<inlineCode parentName="p">{`kp-button-active`}</inlineCode>{`.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2: Create `}<inlineCode parentName="strong">{`sections/buttons.jsx`}</inlineCode></strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/* Copyright © 2017-2026 Kuali, Inc. - All Rights Reserved */
import React from 'react'

import * as Icons from '../../../icons'
import { Section, Swatch } from '../section'

export function ButtonsSection ({ num }) {
  return (
    <Section
      num={num}
      title='Buttons'
      source='app.css:234-352 (.lowkey .kp-button-*); app.css:356-378 (.lowkey-forms .kp-button-transparent)'
      code={
        "<button className='kp-button-primary'>…  variants: -secondary -destructive -outline -ghost -link -with-icon -primary-with-icon -dropdown -transparent · modifiers: kp-button-sm kp-button-icon-only kp-button-active"
      }
    >
      <Swatch label='primary'>
        <button className='kp-button-primary'>Publish</button>
      </Swatch>
      <Swatch label='secondary'>
        <button className='kp-button-secondary'>Cancel</button>
      </Swatch>
      <Swatch label='destructive'>
        <button className='kp-button-destructive'>Delete</button>
      </Swatch>
      <Swatch label='outline'>
        <button className='kp-button-outline'>Options</button>
      </Swatch>
      <Swatch label='ghost'>
        <button className='kp-button-ghost'>Dismiss</button>
      </Swatch>
      <Swatch label='link'>
        <button className='kp-button-link'>Learn more</button>
      </Swatch>
      <Swatch label='disabled'>
        <button className='kp-button-primary' disabled>
          Publish
        </button>
      </Swatch>
      <Swatch label='sm'>
        <button className='kp-button-secondary kp-button-sm'>Apply</button>
      </Swatch>
      <Swatch label='with-icon'>
        <button className='kp-button-with-icon'>
          <Icons.Settings />
          Settings
        </button>
      </Swatch>
      <Swatch label='with-icon active'>
        <button className='kp-button-with-icon kp-button-active'>
          <Icons.Settings />
          Settings
        </button>
      </Swatch>
      <Swatch label='primary-with-icon'>
        <button className='kp-button-primary-with-icon'>
          <Icons.Settings />
          Share
        </button>
      </Swatch>
      <Swatch label='icon-only'>
        <button className='kp-button-outline kp-button-icon-only'>
          <Icons.Settings />
        </button>
      </Swatch>
      <Swatch label='dropdown'>
        <button className='kp-button-dropdown'>
          Actions
          <Icons.MenuDown />
        </button>
      </Swatch>
      <Swatch label='transparent'>
        <button className='kp-button-transparent'>
          <Icons.Settings />
          Tools
        </button>
      </Swatch>
    </Section>
  )
}
`}</code></pre>
    <p>{`(If `}<inlineCode parentName="p">{`Icons.Settings`}</inlineCode>{` / `}<inlineCode parentName="p">{`Icons.MenuDown`}</inlineCode>{` don't exist, pick the closest real exports — check `}<inlineCode parentName="p">{`grep -o "export const [A-Za-z]*" app/src/icons/index.tsx | head -40`}</inlineCode>{` — and verify the icon names against a real usage like `}<inlineCode parentName="p">{`app/src/pages-builder/form/index.jsx:379`}</inlineCode>{`.)`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3: Mount it in `}<inlineCode parentName="strong">{`index.jsx`}</inlineCode></strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { ButtonsSection } from './sections/buttons'
// inside the wrapper div, replacing the placeholder comment:
<ButtonsSection num={1} />
`}</code></pre>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4: Verify against the real platform`}</strong></li>
    </ul>
    <ol>
      <li parentName="ol">{`Open `}<inlineCode parentName="li">{`https://local.kualibuild.com:4001/styleguide`}</inlineCode>{` — all swatches render, hover/disabled behave.`}</li>
      <li parentName="ol">{`Open a real screen using these buttons (form builder header uses `}<inlineCode parentName="li">{`kp-button-primary-with-icon`}</inlineCode>{`; any modal footer uses primary/secondary) and screenshot both. Compare: height (40px / 32px sm), radius (6px), colors (primary = `}<inlineCode parentName="li">{`var(--primary, #0c4a6e)`}</inlineCode>{` — sky-tinted dark, hover `}<inlineCode parentName="li">{`sky-950`}</inlineCode>{`), shadow, gap.`}</li>
      <li parentName="ol">{`Fix discrepancies in the section markup (never in app.css).`}</li>
    </ol>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 5: User approval gate`}</strong></li>
    </ul>
    <p>{`Show the user the section (URL + screenshot). Do not proceed to the next section without an explicit OK.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 6: Commit`}</strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add app/src/pages-builder/styleguide
git commit -m "feat(styleguide): buttons section"
`}</code></pre>
    <hr></hr>
    <h3 {...{
      "id": "task-4-snapshot-export-script"
    }}>{`Task 4: Snapshot export script`}</h3>
    <p><strong parentName="p">{`Files:`}</strong></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`Create: `}<inlineCode parentName="p">{`scripts/export-styleguide/index.mjs`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Modify: `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` (devDependency)`}</p>
      </li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}>
        <p parentName="li"><input parentName="p" {...{
            "type": "checkbox",
            "checked": false,
            "disabled": true
          }}></input>{` `}<strong parentName="p">{`Step 1: Add playwright-core (pure JS — safe across the linux/mac shared node_modules)`}</strong></p>
      </li>
    </ul>
    <p>{`Run: `}<inlineCode parentName="p">{`ssh -p 2223 agent@localhost 'cd ~/workspace/builder-ui && pnpm add -D playwright-core'`}</inlineCode></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2: Create `}<inlineCode parentName="strong">{`scripts/export-styleguide/index.mjs`}</inlineCode></strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`/* Copyright © 2017-2026 Kuali, Inc. - All Rights Reserved */
// Exports the /styleguide page as a self-contained HTML snapshot.
// Run from the repo root ON THE HOST (uses installed Google Chrome):
//   node scripts/export-styleguide/index.mjs [url] [outfile]
import fs from 'node:fs'
import path from 'node:path'

import { chromium } from 'playwright-core'

const url = process.argv[2] || 'https://local.kualibuild.com:4001/styleguide'
const out =
  process.argv[3] ||
  path.resolve(process.cwd(), '../__drops/lowkey-components.html')

const browser = await chromium.launch({
  channel: process.env.PW_CHANNEL || 'chrome',
  executablePath: process.env.PW_EXECUTABLE_PATH || undefined
})
const context = await browser.newContext({
  ignoreHTTPSErrors: true,
  viewport: { width: 1280, height: 900 },
  storageState: process.env.PW_STORAGE_STATE || undefined
})
const page = await context.newPage()
await page.goto(url, { waitUntil: 'networkidle' })
await page.waitForSelector('[data-styleguide-ready]')

const html = await page.evaluate(() => {
  let css = ''
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) css += rule.cssText + '\\n'
    } catch {
      /* cross-origin sheet — skip */
    }
  }
  const doc = document.documentElement.cloneNode(true)
  doc
    .querySelectorAll('script, link[rel="stylesheet"], style')
    .forEach(n => n.remove())
  const style = document.createElement('style')
  style.textContent = css
  doc.querySelector('head').appendChild(style)
  return '<!doctype html>\\n' + doc.outerHTML
})

const banner = \`<!-- GENERATED SNAPSHOT (\${new Date().toISOString()}) — do not edit, do not copy styles.
  Live page: \${url}
  Regenerate: node scripts/export-styleguide/index.mjs
  Source of truth: builder-ui app/src/app.css (.lowkey blocks), app/src/ui/, tailwind.config.js -->\\n\`
fs.writeFileSync(out, banner + html)
console.log(\`wrote \${out} (\${Math.round(fs.statSync(out).size / 1024)} KB)\`)
await browser.close()
`}</code></pre>
    <p>{`Known limitation (acceptable, noted in the file banner): web fonts and images keep their origin URLs, so offline viewing falls back to system fonts; all component styles (the point of the doc) are inlined.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3: Run it and verify`}</strong></li>
    </ul>
    <p>{`Run on the host: `}<inlineCode parentName="p">{`cd ~/.config/af/agents/a1/workspace/builder-ui && node scripts/export-styleguide/index.mjs`}</inlineCode>{`
Expected: `}<inlineCode parentName="p">{`wrote …/__drops/lowkey-components.html (… KB)`}</inlineCode>{`. If Task 1 Step 7 found a login redirect, export a logged-in storage state first (`}<inlineCode parentName="p">{`PW_STORAGE_STATE`}</inlineCode>{`).
Then `}<inlineCode parentName="p">{`open ~/.config/af/agents/a1/workspace/__drops/lowkey-components.html`}</inlineCode>{` — banner + buttons section visually identical to the live page; hover a button to confirm CSS interactivity survived.`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4: Commit`}</strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add scripts/export-styleguide package.json pnpm-lock.yaml
git commit -m "feat(styleguide): HTML snapshot export script"
`}</code></pre>
    <hr></hr>
    <h3 {...{
      "id": "task-5-discoverability-pointers"
    }}>{`Task 5: Discoverability pointers`}</h3>
    <p><strong parentName="p">{`Files:`}</strong></p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`Modify: `}<inlineCode parentName="p">{`CLAUDE.md`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`AGENTS.md`}</inlineCode>{` (check `}<inlineCode parentName="p">{`ls -la CLAUDE.md AGENTS.md`}</inlineCode>{` — if one symlinks the other, edit the real file once)`}</p>
      </li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}>
        <p parentName="li"><input parentName="p" {...{
            "type": "checkbox",
            "checked": false,
            "disabled": true
          }}></input>{` `}<strong parentName="p">{`Step 1: Add this section`}</strong></p>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`## LowkeyUI styleguide

- Live styleguide (dev only): \`https://local.kualibuild.com:4001/styleguide\`
  (port = 4000 + af container index). Renders the real lowkey components with
  per-section source pointers.
- Source of truth for lowkey styles: \`app/src/app.css\` (the \`.lowkey\` /
  \`.lowkey-forms\` blocks), \`app/src/ui/\`, \`app/src/ui/shadcn/\`,
  \`tailwind.config.js\`. Never copy styles from reference docs or snapshots —
  import the components or use the \`kp-*\` classes.
- HTML snapshot for visual verification outside the dev env:
  \`node scripts/export-styleguide/index.mjs\` (writes the workspace
  \`__drops/lowkey-components.html\`).
`}</code></pre>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2: Commit`}</strong></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add CLAUDE.md AGENTS.md
git commit -m "docs: point AI sessions at the lowkey styleguide and style sources"
`}</code></pre>
    <hr></hr>
    <h3 {...{
      "id": "task-6-component-build-loop-repeat-per-inventory-item"
    }}>{`Task 6: Component build loop (repeat per inventory item)`}</h3>
    <p>{`This task repeats for every remaining item in the approved inventory (Task 2), in inventory order — atoms, then molecules (modal/dialog with field form + footer, card/section with labeled fields, table with mixed gadget cells, search/filter bar), then layouts (1/2/3-panel, rendered scaled-down inside a fixed-height bordered frame). The buttons section (Task 3) is the concrete template; per-section code is written at build time because it depends on discovery results and per-section user feedback.`}</p>
    <p>{`For each inventory item:`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 1:`}</strong>{` Read the item's real source (file:line from the inventory) and one real usage in the app. List every variant/state that exists.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2:`}</strong>{` Create `}<inlineCode parentName="li">{`app/src/pages-builder/styleguide/sections/<name>.jsx`}</inlineCode>{` exporting `}<inlineCode parentName="li">{`<NameSection num={N} />`}</inlineCode>{` built from real imports (`}<inlineCode parentName="li">{`app/src/ui/...`}</inlineCode>{`, `}<inlineCode parentName="li">{`ui/shadcn/...`}</inlineCode>{`) or real `}<inlineCode parentName="li">{`kp-*`}</inlineCode>{` markup. Wrap in `}<inlineCode parentName="li">{`formbot-gadget`}</inlineCode>{` / `}<inlineCode parentName="li">{`formbot-config`}</inlineCode>{` divs when the cascade requires it (e.g. `}<inlineCode parentName="li">{`.lowkey-forms .formbot-gadget .kp-input`}</inlineCode>{`). Render interactive-only states statically too (dropdown open, modal visible, datepicker expanded) so the snapshot captures them. Components needing data get minimal inline mock props — no API calls.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3:`}</strong>{` Mount it in `}<inlineCode parentName="li">{`index.jsx`}</inlineCode>{` with the next section number.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4:`}</strong>{` Verify in the browser against a real platform screen using that component (screenshot both, compare sizes/colors/spacing/states). Fix the section, never the platform styles. Real platform bugs found get reported to the user, not fixed here.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 5:`}</strong>{` User approval gate — show the section, wait for OK.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 6:`}</strong>{` Commit: `}<inlineCode parentName="li">{`git commit -m "feat(styleguide): <name> section"`}</inlineCode>{`.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 7:`}</strong>{` Tick the item off in the inventory doc (amend the same commit or batch inventory updates every few sections).`}</li>
    </ul>
    <p>{`After the `}<strong parentName="p">{`final`}</strong>{` section: re-run the export script (Task 4 Step 3), verify the snapshot end-to-end, and update the smoke test to assert one element from the first and last sections.`}</p>
    <hr></hr>
    <h3 {...{
      "id": "task-7-wrap-up"
    }}>{`Task 7: Wrap-up`}</h3>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 1:`}</strong>{` Run the full test suite: `}<inlineCode parentName="li">{`ssh -p 2223 agent@localhost 'cd ~/workspace/builder-ui && pnpm test:no-coverage'`}</inlineCode>{` — expected: green (pre-existing failures, if any, noted to the user).`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 2:`}</strong>{` Regenerate the snapshot one last time; confirm `}<inlineCode parentName="li">{`__drops/lowkey-components.html`}</inlineCode>{` opens standalone with every section visible.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 3:`}</strong>{` Update the spec's status line and commit any doc drift.`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<strong parentName="li">{`Step 4:`}</strong>{` Hand off via superpowers:finishing-a-development-branch (merge/PR decision belongs to the user — note the branch stacks on `}<inlineCode parentName="li">{`fix/lowkey-rollout-bugfixes`}</inlineCode>{`).`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      